import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon, CheckCircleIcon, ArrowUpTrayIcon, BanknotesIcon,
  ClockIcon, ExclamationTriangleIcon, CalendarDaysIcon,
  LockClosedIcon, XMarkIcon, ArrowDownTrayIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tm-root * { box-sizing:border-box; }
  .tm-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tmFade .4s ease both; }
  @keyframes tmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tm-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#7880a4; cursor:pointer; margin-bottom:1.4rem; border:none; background:none; font-family:'Sora',sans-serif; padding:0; transition:color .15s; }
  .tm-back:hover { color:#0d9488; }
  .tm-back svg { width:14px; height:14px; stroke-width:2.5; }
  .tm-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .tm-title-wrap { display:flex; align-items:center; gap:12px; }
  .tm-avatar { width:46px; height:46px; border-radius:13px; background:#f0fdfa; color:#0d9488; display:flex; align-items:center; justify-content:center; font-size:.95rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .tm-name  { font-size:1.25rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tm-month { font-size:.75rem; color:#9094a8; margin-top:2px; }
  .tm-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-teal  { background:#0d9488; color:white; }
  .btn-teal:hover  { background:#0b7b71; box-shadow:0 4px 14px rgba(13,148,136,.35); transform:translateY(-1px); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  .own-salary-notice { display:flex; align-items:center; gap:10px; padding:12px 16px; border-radius:12px; margin-bottom:1.25rem; font-size:.8rem; font-weight:600; background:#fffbeb; color:#92400e; border:1px solid #fde68a; }
  .req-notice { background:#f0fdfa; border:1px solid #99f6e4; border-radius:12px; padding:10px 16px; font-size:.76rem; color:#0f766e; margin-bottom:1.25rem; }
  .finalized-banner { display:flex; align-items:center; gap:10px; padding:12px 16px; border-radius:12px; margin-bottom:1.25rem; font-size:.8rem; font-weight:600; flex-wrap:wrap; }
  .finalized-banner.green { background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; }
  .finalized-banner.blue  { background:#eef2ff; color:#3730a3; border:1px solid #c7d2fe; }
  .finalized-banner svg   { width:16px; height:16px; stroke-width:2; flex-shrink:0; }
  .fb-meta { font-size:.7rem; font-weight:400; color:#6b7194; margin-top:1px; }
  .stat-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; position:relative; overflow:hidden; animation:tmFade .4s ease both; }
  .stat-card::after { content:''; position:absolute; top:0; right:0; width:60px; height:60px; border-radius:0 14px 0 60px; opacity:.06; }
  .stat-card.blue::after{background:#3b6ef8} .stat-card.green::after{background:#0d9488}
  .stat-card.amber::after{background:#f59e0b} .stat-card.red::after{background:#ef4444}
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.blue{background:#eef2ff;color:#3b6ef8} .s-icon.green{background:#f0fdfa;color:#0d9488}
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.red{background:#fef2f2;color:#ef4444}
  .s-label { font-size:.68rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:2px; }
  .s-value { font-size:1.2rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; line-height:1; font-family:'JetBrains Mono',monospace; }
  .s-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }
  .table-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.4rem; }
  .table-panel-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .tp-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .tp-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .day-table { width:100%; border-collapse:collapse; }
  .day-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .day-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .day-table th.right { text-align:right; }
  .day-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .day-table tbody tr:last-child { border-bottom:none; }
  .day-table tbody tr:hover { background:#fafbff; }
  .day-table td { padding:10px 14px; vertical-align:middle; font-size:.79rem; text-align:center !important; }
  .day-table td.right { text-align:right; }
  .day-date { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:500; color:#1a1d2e; }
  .day-dow  { font-size:.67rem; color:#9094a8; margin-top:1px; }
  .total-row td { background:#fafbff; font-weight:700; border-top:2px solid #e8ebf4 !important; text-align:center !important; }
  .day-cards { display:none; padding:10px; }
  .day-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .dc-top    { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .dc-date   { font-family:'JetBrains Mono',monospace; font-size:.8rem; font-weight:600; color:#0f1123; }
  .dc-dow    { font-size:.67rem; color:#9094a8; }
  .dc-row    { display:flex; align-items:center; justify-content:space-between; font-size:.75rem; padding:3px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .dc-row:last-of-type { border-bottom:none; }
  .dc-label  { color:#9094a8; font-weight:600; white-space:nowrap; }
  .dc-net    { font-family:'JetBrains Mono',monospace; font-size:.9rem; font-weight:700; color:#0d9488; }
  .day-total-card { background:white; border-radius:12px; border:2px solid #e8ebf4; padding:13px; margin:0 10px 10px; }
  .dtc-row { display:flex; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f3f5fb; gap:8px; }
  .dtc-row:last-child { border-bottom:none; }
  .dtc-label { color:#9094a8; font-weight:600; }
  .status-pill { display:inline-flex; align-items:center; font-size:.67rem; font-weight:600; padding:2px 9px; border-radius:99px; }
  .status-pill.present  { background:#ecfdf5; color:#10c98e; }
  .status-pill.absent   { background:#fef2f2; color:#ef4444; }
  .status-pill.on-leave { background:#eef2ff; color:#3b6ef8; }
  .status-pill.holiday  { background:#f3f0ff; color:#8b5cf6; }
  .leave-type-pill { display:inline-flex; font-size:.63rem; font-weight:600; padding:1px 6px; border-radius:99px; margin-left:4px; }
  .leave-type-pill.paid   { background:#ecfdf5; color:#10c98e; }
  .leave-type-pill.unpaid { background:#fef2f2; color:#ef4444; }
  .mono-sm  { font-family:'JetBrains Mono',monospace; font-size:.75rem; color:#6b7194; }
  .ot-badge { display:inline-flex; align-items:center; font-family:'JetBrains Mono',monospace; font-size:.68rem; font-weight:600; padding:2px 7px; border-radius:99px; background:#f3f0ff; color:#8b5cf6; }
  .amt       { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .amt.red   { color:#ef4444; }
  .amt.green { color:#0d9488; font-size:.82rem; }
  .amt.dim   { color:#c0c5dc; font-weight:400; }
  .amt.purple{ color:#8b5cf6; }
  .tm-empty { text-align:center; padding:3.5rem 2rem; }
  .tm-empty-icon  { width:48px; height:48px; border-radius:13px; background:#f0fdfa; color:#0d9488; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .tm-empty-icon svg { width:22px; height:22px; stroke-width:1.6; }
  .tm-empty-title { font-size:.9rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .tm-empty-sub   { font-size:.76rem; color:#9094a8; }
  .tm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; }
  .modal-box  { background:white; border-radius:20px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:moIn .25s ease; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body  { padding:20px 22px; }
  .modal-footer{ display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }
  .form-field  { display:flex; flex-direction:column; gap:5px; margin-bottom:12px; }
  .form-label  { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus { border-color:#0d9488; background:white; }
  .form-row    { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  @media (max-width:1100px) { .stat-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:768px) {
    .tm-name { font-size:1.05rem; }
    .stat-grid { grid-template-columns:repeat(2,1fr); }
    .day-table-wrap { display:none !important; }
    .day-cards { display:block; }
    .modal-box { max-width:100%; border-radius:16px 16px 0 0; max-height:95vh; }
    .modal-backdrop { padding:0 10px; align-items:flex-end; }
    .form-row { grid-template-columns:1fr; }
  }
  @media (max-width:480px) { .stat-grid { grid-template-columns:1fr 1fr; } }
`;

const initials  = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';
const fmt       = (n)    => `Rs. ${(n||0).toLocaleString('en-PK', { minimumFractionDigits:2, maximumFractionDigits:2 })}`;
const fmtTime   = (d)    => d ? new Date(d).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', hour12:true }) : '—';
const fmtDate   = (d)    => new Date(d).toLocaleDateString('en-US', { day:'numeric', month:'short' });
const dayOfWeek = (d)    => new Date(d).toLocaleDateString('en-US', { weekday:'short' });
const monthLabel= (m)    => { const[y,mn]=m.split('-').map(Number); return new Date(y,mn-1,1).toLocaleDateString('en-US',{month:'long',year:'numeric'}); };

export default function FinanceSalaryMonth() {
  const { teacherId, month } = useParams();
  const [searchParams]       = useSearchParams();
  const navigate             = useNavigate();
  const { user }             = useAuth();

  const role     = searchParams.get('role')     || 'teacher';
  const isPortal = searchParams.get('isPortal') !== 'false';

  // ── If finance user is viewing their OWN salary, block all edit/request actions ──
  const isOwnSalary = user?._id?.toString() === teacherId?.toString();

  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [reqModal, setReqModal] = useState(null);
  const [note,     setNote]     = useState('');
  const [saving,   setSaving]   = useState(false);
  const [addOpen,  setAddOpen]  = useState(false);
  const [addForm,  setAddForm]  = useState({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present', note:'' });
  const [adding,   setAdding]   = useState(false);

  useEffect(() => { fetchDetail(); }, [teacherId, month]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      // Use new unified endpoint
      const { data: res } = await api.get(
        `/finance/salary/${teacherId}/month/${month}?role=${role}&isPortal=${isPortal}`
      );
      setData(res);
    } catch { toast.error('Failed to load month detail'); }
    finally  { setLoading(false); }
  };

  const handleExportCSV = () => {
    if (!data?.days?.length) return toast.error('No records to export');
    const headers = ['Date','Day','Status','Check In','Check Out','Work Hrs','OT Hrs','Late Min','Early Exit Min','Regular Pay','OT Pay','Deductions','Net'];
    const rows    = data.days.map(d => [
      fmtDate(d.date), dayOfWeek(d.date), d.status,
      fmtTime(d.checkIn), fmtTime(d.checkOut),
      d.workHours?.toFixed(2), d.overtimeHours?.toFixed(2),
      d.lateMinutes, d.earlyMinutes,
      d.regularPay?.toFixed(2), d.overtimePay?.toFixed(2),
      d.deductions?.toFixed(2), d.netDay?.toFixed(2),
    ]);
    const csv  = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `salary-${teacher?.name?.replace(/\s+/g,'-')}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAddDay = async () => {
    if (!addForm.date) return toast.error('Please select a date');
    setAdding(true);
    try {
      await api.post('/finance/requests', {
        type: 'salary_action',
        payload: {
          action:      'add_attendance',
          teacherId,
          role, isPortal,
          month,
          teacherName: teacher?.name,
          attendance: {
            date:             addForm.date,
            checkIn:          addForm.checkIn || undefined,
            checkOut:         addForm.checkOut || undefined,
            lateMinutes:      addForm.lateMinutes,
            earlyExitMinutes: addForm.earlyExitMinutes,
            status:           addForm.status,
          },
        },
        note: addForm.note || `Add attendance record for ${teacher?.name} on ${addForm.date}`,
      });
      toast.success('Add day request submitted ✓');
      setAddOpen(false);
      setAddForm({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present', note:'' });
    } catch { toast.error('Failed to submit'); }
    finally { setAdding(false); }
  };

  const submitRequest = async () => {
    try {
      setSaving(true);
      await api.post('/finance/requests', {
        type: 'salary_action',
        payload: { action: reqModal.action, teacherId, role, isPortal, month, teacherName: data?.teacher?.name },
        note: note || reqModal.defaultNote,
      });
      toast.success('Request submitted for admin approval ✓');
      setReqModal(null); setNote('');
    } catch { toast.error('Failed to submit'); }
    finally { setSaving(false); }
  };

  const openReq = (action, title, defaultNote) => {
    setReqModal({ action, title, defaultNote });
    setNote('');
  };

  if (loading) return <div className="tm-root"><style>{style}</style><div className="tm-loading"><div className="spinner"/>Loading…</div></div>;
  if (!data)   return null;

  const { teacher, days, totals, savedRecord } = data;
  const isFinalized = !!savedRecord;

  return (
    <div className="tm-root"><style>{style}</style>

      <button className="tm-back" onClick={() => navigate(`/finance/salary/${teacherId}?role=${role}&isPortal=${isPortal}`)}>
        <ChevronLeftIcon />Back to Months
      </button>

      <div className="tm-header">
        <div className="tm-title-wrap">
          <div className="tm-avatar">{initials(teacher?.name)}</div>
          <div>
            <div className="tm-name">{teacher?.name}</div>
            <div className="tm-month">{monthLabel(month)}{teacher?.employeeId ? ` · ${teacher.employeeId}` : ''}</div>
          </div>
        </div>

        {/* Action buttons — hidden if viewing own salary */}
        <div className="tm-actions">
          <button className="btn btn-ghost" onClick={handleExportCSV}><ArrowDownTrayIcon />Export CSV</button>
          {!isOwnSalary && (
            <>
              <button className="btn btn-green"
                onClick={() => { setAddOpen(true); setAddForm({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present', note:'' }); }}>
                <PlusCircleIcon />Request Add Day
              </button>
              {isFinalized ? (
                <>
                  {savedRecord.status === 'unpaid'
                    ? <button className="btn btn-green" onClick={() => openReq('mark_paid', 'Request Mark as Paid', `Mark ${teacher?.name} ${monthLabel(month)} salary as paid`)}><CheckCircleIcon />Request Mark Paid</button>
                    : <button className="btn btn-ghost" style={{ color:'#ef4444', border:'1px solid #fecaca' }} onClick={() => openReq('mark_unpaid', 'Request Mark as Unpaid', `Mark ${teacher?.name} ${monthLabel(month)} salary as unpaid`)}>Mark Unpaid</button>
                  }
                  <button className="btn btn-teal" onClick={() => openReq('refinalize', 'Request Re-finalize', `Re-finalize salary for ${teacher?.name} — ${monthLabel(month)}`)}><ArrowUpTrayIcon />Request Re-finalize</button>
                </>
              ) : (
                <button className="btn btn-teal" onClick={() => openReq('finalize', 'Request Finalize Salary', `Finalize salary for ${teacher?.name} — ${monthLabel(month)}`)}><LockClosedIcon />Request Finalize</button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Own salary notice — shown instead of action buttons */}
      {isOwnSalary && (
        <div className="own-salary-notice">
          🔒 This is your own salary record. Only admins can edit or finalize salary records.
        </div>
      )}

      {!isOwnSalary && (
        <div className="req-notice">📋 All actions on this page require admin approval before taking effect.</div>
      )}

      {isFinalized && (
        <div className={`finalized-banner ${savedRecord.status === 'paid' ? 'blue' : 'green'}`}>
          {savedRecord.status === 'paid' ? <CheckCircleIcon/> : <LockClosedIcon/>}
          <div>
            <div>{savedRecord.status === 'paid' ? `Paid on ${fmtDate(savedRecord.paidAt)} by ${savedRecord.paidBy}` : 'Finalized — awaiting payment'}</div>
            <div className="fb-meta">Finalized on {fmtDate(savedRecord.finalizedAt)} by {savedRecord.finalizedBy}</div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="stat-grid">
        <div className="stat-card blue"><div className="s-icon blue"><CalendarDaysIcon/></div><div className="s-label">Present</div><div className="s-value">{totals?.daysPresent||0}</div><div className="s-sub">{totals?.daysAbsent||0} absent · {totals?.daysOnLeave||0} on leave</div></div>
        <div className="stat-card green"><div className="s-icon green"><ClockIcon/></div><div className="s-label">Total Hours</div><div className="s-value">{totals?.totalHours||0}</div><div className="s-sub">{totals?.totalOvertimeHours||0} overtime</div></div>
        <div className="stat-card amber"><div className="s-icon amber"><ExclamationTriangleIcon/></div><div className="s-label">Late Minutes</div><div className="s-value">{totals?.totalLateMinutes||0}</div><div className="s-sub">{totals?.totalEarlyMinutes||0} early exit</div></div>
        <div className="stat-card red"><div className="s-icon red"><BanknotesIcon/></div><div className="s-label">Deductions</div><div className="s-value" style={{fontSize:'.9rem'}}>{fmt(totals?.deductions)}</div><div className="s-sub">penalties</div></div>
        <div className="stat-card green"><div className="s-icon green"><BanknotesIcon/></div><div className="s-label">Net Salary</div><div className="s-value" style={{fontSize:'.9rem',color:'#0d9488'}}>{fmt(totals?.netSalary)}</div><div className="s-sub">this month</div></div>
      </div>

      {/* Day table */}
      <div className="table-panel">
        <div className="table-panel-header">
          <div><div className="tp-title">Day-by-Day Breakdown</div><div className="tp-sub">{days?.length||0} records</div></div>
        </div>

        {!days?.length ? (
          <div className="tm-empty"><div className="tm-empty-icon"><CalendarDaysIcon/></div><div className="tm-empty-title">No attendance records</div><div className="tm-empty-sub">No check-ins found for this month.</div></div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="day-table-wrap" style={{overflowX:'auto'}}>
              <table className="day-table">
                <thead><tr>
                  <th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th>
                  <th>Hours</th><th>Overtime</th><th>Late</th><th>Early Exit</th>
                  <th>Regular Pay</th><th>OT Pay</th><th>Deductions</th><th className="right">Net</th>
                </tr></thead>
                <tbody>
                  {days.map((d, i) => (
                    <tr key={i}>
                      <td><div className="day-date">{fmtDate(d.date)}</div><div className="day-dow">{dayOfWeek(d.date)}</div></td>
                      <td><span className={`status-pill ${d.status}`}>{d.status}</span>{d.status==='on-leave'&&d.leaveType&&<span className={`leave-type-pill ${d.leaveType}`}>{d.leaveType}</span>}</td>
                      <td><span className="mono-sm">{fmtTime(d.checkIn)}</span></td>
                      <td><span className="mono-sm">{fmtTime(d.checkOut)}</span></td>
                      <td><span className="mono-sm">{d.workHours?.toFixed(2)} hrs</span></td>
                      <td>{d.overtimeHours>0?<span className="ot-badge">+{d.overtimeHours?.toFixed(2)}</span>:<span className="amt dim">—</span>}</td>
                      <td><span className="mono-sm" style={{color:d.lateMinutes>0?'#f59e0b':'#b0b5cc'}}>{d.lateMinutes>0?`${d.lateMinutes}m`:'—'}</span></td>
                      <td><span className="mono-sm" style={{color:d.earlyMinutes>0?'#ef4444':'#b0b5cc'}}>{d.earlyMinutes>0?`${d.earlyMinutes}m`:'—'}</span></td>
                      <td><span className="amt">{fmt(d.regularPay)}</span></td>
                      <td>{d.overtimePay>0?<span className="amt purple">{fmt(d.overtimePay)}</span>:<span className="amt dim">—</span>}</td>
                      <td>{d.deductions>0?<span className="amt red">−{fmt(d.deductions)}</span>:<span className="amt dim">—</span>}</td>
                      <td className="right"><span className="amt green">{fmt(d.netDay)}</span></td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan={4}><span style={{fontFamily:'Sora',fontWeight:700,fontSize:'.8rem',color:'#0f1123'}}>Monthly Total</span><div style={{fontSize:'.67rem',color:'#9094a8',marginTop:2}}>{totals?.daysPresent} present · {totals?.daysAbsent} absent · {totals?.daysOnLeave||0} on leave</div></td>
                    <td><span className="mono-sm" style={{fontWeight:700,color:'#0f1123'}}>{totals?.totalHours} hrs</span></td>
                    <td>{totals?.totalOvertimeHours>0?<span className="ot-badge">+{totals.totalOvertimeHours}</span>:<span className="amt dim">—</span>}</td>
                    <td><span className="mono-sm" style={{color:'#f59e0b',fontWeight:700}}>{totals?.totalLateMinutes>0?`${totals.totalLateMinutes}m`:'—'}</span></td>
                    <td><span className="mono-sm" style={{color:'#ef4444',fontWeight:700}}>{totals?.totalEarlyMinutes>0?`${totals.totalEarlyMinutes}m`:'—'}</span></td>
                    <td><span className="amt" style={{fontWeight:700}}>{fmt(totals?.regularPay)}</span></td>
                    <td>{totals?.overtimePay>0?<span className="amt purple" style={{fontWeight:700}}>{fmt(totals.overtimePay)}</span>:<span className="amt dim">—</span>}</td>
                    <td>{totals?.deductions>0?<span className="amt red" style={{fontWeight:700}}>−{fmt(totals.deductions)}</span>:<span className="amt dim">—</span>}</td>
                    <td className="right"><span className="amt green" style={{fontWeight:700,fontSize:'.9rem'}}>{fmt(totals?.netSalary)}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="day-cards">
              {days.map((d, i) => (
                <div key={i} className="day-card">
                  <div className="dc-top">
                    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                      <div><div className="dc-date">{fmtDate(d.date)}</div><div className="dc-dow">{dayOfWeek(d.date)}</div></div>
                      <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                        <span className={`status-pill ${d.status}`}>{d.status}</span>
                        {d.status==='on-leave'&&d.leaveType&&<span className={`leave-type-pill ${d.leaveType}`}>{d.leaveType}</span>}
                      </div>
                    </div>
                  </div>
                  {d.checkIn&&<div className="dc-row"><span className="dc-label">Check In/Out</span><span className="mono-sm">{fmtTime(d.checkIn)} → {fmtTime(d.checkOut)}</span></div>}
                  <div className="dc-row"><span className="dc-label">Hours</span><span className="mono-sm">{d.workHours?.toFixed(2)}h{d.overtimeHours>0?<> · <span style={{color:'#8b5cf6'}}>+{d.overtimeHours?.toFixed(2)} OT</span></>:''}</span></div>
                  {d.lateMinutes>0&&<div className="dc-row"><span className="dc-label">Late</span><span style={{color:'#f59e0b',fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',fontWeight:600}}>{d.lateMinutes}m</span></div>}
                  {d.deductions>0&&<div className="dc-row"><span className="dc-label">Deduction</span><span className="amt red">−{fmt(d.deductions)}</span></div>}
                  <div className="dc-row"><span className="dc-label">Net Pay</span><span className="dc-net">{fmt(d.netDay)}</span></div>
                </div>
              ))}
              <div className="day-total-card">
                <div style={{fontWeight:700,fontSize:'.82rem',color:'#0f1123',marginBottom:8}}>Monthly Total</div>
                <div className="dtc-row"><span className="dtc-label">Total Hours</span><span className="mono-sm" style={{fontWeight:700}}>{totals?.totalHours}h</span></div>
                <div className="dtc-row"><span className="dtc-label">Regular Pay</span><span className="amt">{fmt(totals?.regularPay)}</span></div>
                {totals?.overtimePay>0&&<div className="dtc-row"><span className="dtc-label">OT Pay</span><span className="amt purple">{fmt(totals.overtimePay)}</span></div>}
                {totals?.deductions>0&&<div className="dtc-row"><span className="dtc-label">Deductions</span><span className="amt red">−{fmt(totals.deductions)}</span></div>}
                <div className="dtc-row" style={{borderTop:'1.5px solid #e8ebf4',marginTop:4,paddingTop:8}}>
                  <span className="dtc-label" style={{color:'#0f1123',fontWeight:700}}>Net Salary</span>
                  <span className="amt green" style={{fontSize:'.9rem',fontWeight:700}}>{fmt(totals?.netSalary)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Day Modal — only shown when !isOwnSalary */}
      {addOpen && !isOwnSalary && createPortal(
        <div className="modal-backdrop" onClick={() => setAddOpen(false)}>
          <div className="modal-box" style={{maxWidth:520}} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Request Add Attendance Record</div>
              <button className="modal-close" onClick={() => setAddOpen(false)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              <div style={{background:'#f0fdfa',border:'1px solid #99f6e4',borderRadius:10,padding:'10px 14px',fontSize:'.76rem',color:'#0f766e',marginBottom:14}}>
                📋 This will be sent to admin for approval before the record is created.
              </div>
              <div className="form-row">
                <div className="form-field" style={{gridColumn:'1/-1'}}>
                  <label className="form-label">Date</label>
                  <input type="date" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.date} min={`${month}-01`} max={`${month}-31`}
                    onChange={e => setAddForm(p => ({...p, date:e.target.value}))} />
                  <span style={{fontSize:'.67rem',color:'#b0b5cc'}}>Must be within {monthLabel(month)}</span>
                </div>
                <div className="form-field" style={{gridColumn:'1/-1'}}>
                  <label className="form-label">Status</label>
                  <select className="form-select" value={addForm.status} onChange={e => setAddForm(p => ({...p, status:e.target.value}))}>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Check In</label>
                  <input type="time" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.checkIn} onChange={e => setAddForm(p => ({...p, checkIn:e.target.value}))} />
                </div>
                <div className="form-field">
                  <label className="form-label">Check Out</label>
                  <input type="time" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.checkOut} onChange={e => setAddForm(p => ({...p, checkOut:e.target.value}))} />
                  <span style={{fontSize:'.67rem',color:'#b0b5cc'}}>Work hours auto-calculated</span>
                </div>
                <div className="form-field">
                  <label className="form-label">Late Minutes</label>
                  <input type="number" min="0" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.lateMinutes} onChange={e => setAddForm(p => ({...p, lateMinutes:e.target.value}))} />
                </div>
                <div className="form-field">
                  <label className="form-label">Early Exit Min</label>
                  <input type="number" min="0" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.earlyExitMinutes} onChange={e => setAddForm(p => ({...p, earlyExitMinutes:e.target.value}))} />
                </div>
                <div className="form-field" style={{gridColumn:'1/-1'}}>
                  <label className="form-label">Note to Admin</label>
                  <textarea className="form-input" rows={2} style={{resize:'vertical'}}
                    value={addForm.note} onChange={e => setAddForm(p => ({...p, note:e.target.value}))}
                    placeholder={`e.g. Staff was present but forgot to check in on ${addForm.date||'this day'}`} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAddOpen(false)}>Cancel</button>
              <button className="btn btn-teal" onClick={handleAddDay} disabled={adding}>
                <ArrowUpTrayIcon />{adding ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Request Action Modal — only shown when !isOwnSalary */}
      {reqModal && !isOwnSalary && createPortal(
        <div className="modal-backdrop" onClick={() => setReqModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{reqModal.title}</div>
              <button className="modal-close" onClick={() => setReqModal(null)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              <div style={{background:'#f0fdfa',border:'1px solid #99f6e4',borderRadius:10,padding:'10px 14px',fontSize:'.76rem',color:'#0f766e',marginBottom:14}}>
                📋 This action will be sent to admin for approval before taking effect.
              </div>
              <div style={{background:'#f8f9fc',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:'.82rem'}}>
                <strong>{teacher?.name}</strong> — {monthLabel(month)}
                {isFinalized && <span style={{marginLeft:8,fontSize:'.72rem',background:savedRecord.status==='paid'?'#eef2ff':'#ecfdf5',color:savedRecord.status==='paid'?'#3b6ef8':'#065f46',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{savedRecord.status}</span>}
              </div>
              <div className="form-field">
                <label className="form-label">Note to Admin</label>
                <textarea className="form-input" rows={3} style={{resize:'vertical'}}
                  value={note} onChange={e => setNote(e.target.value)}
                  placeholder={reqModal.defaultNote} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setReqModal(null)}>Cancel</button>
              <button className="btn btn-teal" onClick={submitRequest} disabled={saving}>
                <ArrowUpTrayIcon />{saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}